import { useCallback, useContext, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Dialog } from '@/components/ui/dialog'
import { SortDescriptor } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { PageData } from 'api/Pagination/Types'
import { TransactionDto, TransactionFilterKey } from 'api/Transaction/Types/Transaction'
import { Pager } from 'components/components/Pager'
import { Modal } from 'components/CustomModal'
import { FilterType } from 'components/Filter'
import { SelectAssigneeFilter } from 'components/Filter/ClientFields/SelectAssigneeFilter'
import {
  SelectClientFilter,
  SelectClientModalOpenEvent,
} from 'components/Filter/ClientFields/SelectClientFilter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { getCrossWalletFields } from 'components/Filter/crossWalletFields'
import {
  SelectPaymentAgentFilter,
  SelectPaymentAgentModalOpenEvent,
} from 'components/Filter/PaymentAgentFields/SelectPaymentAgentFilter'
import {
  SelectWalletFilter,
  SelectWalletModalOpenEvent,
} from 'components/Filter/WalletFields/SelectWalletFilter'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { generateClassNameForReverseType } from 'helpers/transactions'
import { isGoBack } from 'lib/react-router-dom'
import {
  SelectPaymentAgentEvent,
  SelectPaymentAgentTable,
} from 'routes/Records/PaymentAgent/SelecPaymentAgentTable'
import { useAppDispatch, useAppSelector } from 'store'
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  GridFilterActionState,
  resetGridFilters,
  selectGridFilterByFilterId,
  selectGridFilters,
  setGridFilters,
} from 'store/grid'
import {
  fetchTransactionStatusAction,
  fetchTransactionTypeAction,
} from 'store/transactions/transaction.action'
import { GridFilterOperatorEnum, GridFilterOperatorNameEnum } from 'types/GridFilterOperatorEnum'
import { PageQuery } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'
import { isTransactionCompletedStatus } from 'utils/transaction/status.index'

import { CustomColumnMenu } from '../Records/Clients/CustomColumnMenu'
import { SelectClientEvent, SelectClientTable } from '../Records/Clients/SelectClientTable'
import SelectWalletTable, { SelectWalletEvent } from '../Records/Wallets/SelectWalletTable'
import { normalizeTransactions, normalizeTransactionsSortQuery } from './normalizer'
import paymentAgentColumn from './paymentAgentColumn'
import {
  SelectTransactionsAssignTable,
  SelectTransactionsEvent,
} from './SelectTransactionAssignTable'
import WalletTransactionReversePage from './TransactionReverse/WalletTransactionReversePage'
import { GridColumn } from './walletsTransactionColumn'

const useMapStateToProps = () => ({
  tradingAccountClassificationType: useAppSelector(
    (state) => state.tradingAccounts.tradingAccountClassificationType
  ),
  gridState: useAppSelector((state) => state.grid),
  gridFilterByFilterIdSelector: useAppSelector(selectGridFilterByFilterId),
  hasFilters: useAppSelector(selectGridFilters).length > 0,
  transactionStatus: useAppSelector((state) => state.transactions.status),
  transactionTypes: useAppSelector((state) => state.transactions.types),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const walletsApiClient = useApiClient(WalletsApiClient)
  return {
    fetchTransactionStatus: async () => dispatch(fetchTransactionStatusAction(walletsApiClient)),
    fetchTransactionTypes: async () => dispatch(fetchTransactionTypeAction(walletsApiClient)),
    setGridFilters: async (state: GridFilterActionState) => dispatch(setGridFilters(state)),
    resetGridFilters: async () => dispatch(resetGridFilters()),
  }
}

const PaymentAgentTransactionsOverview = (): JSX.Element => {
  const [auth] = useContext(AuthSessionContext)
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })

  const walletsApiClient = useApiClient(WalletsApiClient)
  const permissions = auth?.permissions || []
  const [transactions, setTransactions] = useState<TransactionDto[]>([])
  const [columns, setColumns] = useState<GridColumn[]>([])

  const [isPaymentAgentModalOpen, setPaymentAgentModalOpen] = useState<boolean>(false)
  const [isClientModalOpen, setClientModalOpen] = useState<boolean>(false)
  const [isAssigneeModalOpen, setAssigneeModalOpen] = useState<boolean>(false)
  const [isWalletModalOpen, setWalletModalOpen] = useState<boolean>(false)
  const [reverseTransactionModal, setReverseTransactionModal] = useState<{
    data?: TransactionDto
    visible: boolean
  }>({ data: undefined, visible: false })

  const [paymentAgentModalFilterId, setPaymentAgentModalFilterId] = useState<string>()
  const [clientModalFilterId, setClientModalFilterId] = useState<string>()
  const [assignModalFilterId, setAssignModalFilterId] = useState<string>()
  const [walletModalFilterId, setWalletModalFilterId] = useState<string>()

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)
  const { transactionStatus, transactionTypes, gridFilterByFilterIdSelector } = useMapStateToProps()
  const { fetchTransactionStatus, fetchTransactionTypes } = useMapDispatchToProps()

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (gridFilterState: GridFilterActionState): Promise<PageData<TransactionDto[]>> => {
          const searchQuery = getSearchQuery(gridFilterState.filter)
          const sortQuery = getSort(gridFilterState.sort)
          const response = await walletsApiClient.getTransactions('payment-agent-transfers', {
            search: searchQuery,
            sort: sortQuery,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.pageSize,
            skipCount: tableData?.skipCount !== false,
          })

          if (isEmpty(transactionStatus)) await fetchTransactionStatus()
          if (isEmpty(transactionTypes)) await fetchTransactionTypes()

          setColumns(paymentAgentColumn)
          if (response?.items) {
            setTransactions(normalizeTransactions(response.items))
          }
          setTableData({ ...tableData, meta: response })

          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<TransactionDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )
    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getSearchQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(normalizeTransactionsSortQuery(sortDescriptors ?? gridState.sort))
    },
    [gridState.sort]
  )

  const filterFetch = useCallback(
    async (query?: PageQuery) => {
      const searchQuery = query?.search !== undefined ? query.search : getSearchQuery()
      const sortQuery = query?.sort ?? getSort(gridState.sort)
      const pageIndex = query?.pageIndex ?? gridState.pageIndex ?? 1
      const pageSize = query?.pageSize ?? gridState.pageSize ?? 10
      const take = pageSize
      const skip = (pageIndex - 1) * pageSize

      const response = await walletsApiClient.getTransactions('payment-agent-transfers', {
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })
      const total = response.totalResults

      if (response?.items) {
        setTransactions(normalizeTransactions(response.items))
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getSearchQuery,
      getSort,
      gridActions,
      gridState.pageSize,
      gridState.sort,
      gridState.take,
      tableData?.skipCount,
    ]
  )

  const handleColumnsSubmit = (columnsState: GridColumn[]) => {
    setColumns(columnsState)
  }

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    const searchQuery = getSearchQuery(gridState.filter)
    const sortQuery = getSort(event.sort)
    const response = await walletsApiClient.getTransactions('payment-agent-transfers', {
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    if (response?.items) {
      setTransactions(normalizeTransactions(response.items))
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getSearchQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await walletsApiClient.getTransactions('payment-agent-transfers', {
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setTransactions(normalizeTransactions(response.items))
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getSearchQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await walletsApiClient.getTransactions('payment-agent-transfers', {
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    if (response?.items) {
      setTransactions(normalizeTransactions(response.items))
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }
  const handleGridPageChange = async (page: number) => {
    const searchQuery = getSearchQuery()
    const sortQuery = getSort(gridState.sort)
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })
    const response = await walletsApiClient.getTransactions('payment-agent-transfers', {
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setTransactions(normalizeTransactions(response.items))
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePaymentAgentModalOpen = (event: SelectPaymentAgentModalOpenEvent) => {
    setPaymentAgentModalOpen(true)
    setPaymentAgentModalFilterId(event.filterId)
  }

  const handlePaymentAgentModalClose = () => {
    setPaymentAgentModalOpen(false)
    setPaymentAgentModalFilterId(undefined)
  }

  const handleSelectedPaymentAgents = (event: SelectPaymentAgentEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
    }
  }

  const handleClientModalOpen = (event: SelectClientModalOpenEvent) => {
    setClientModalOpen(true)
    setClientModalFilterId(event.filterId)
  }

  const handleClientModalClose = () => {
    setClientModalOpen(false)
    setClientModalFilterId(undefined)
  }

  const handleWalletModalOpen = (event: SelectWalletModalOpenEvent) => {
    setWalletModalOpen(true)
    setWalletModalFilterId(event.filterId)
  }

  const handleWalletModalClose = () => {
    setWalletModalOpen(false)
    setWalletModalFilterId(undefined)
  }

  const handleReverseCompleteTransactionClose = async () => {
    setReverseTransactionModal({ data: undefined, visible: false })
    await filterFetch()
  }

  const handleAssigneeModalOpen = (event: SelectWalletModalOpenEvent) => {
    setAssigneeModalOpen(true)
    setAssignModalFilterId(event.filterId)
  }

  const handleSelectAssignUsersModalClose = () => {
    setAssigneeModalOpen(false)
    setAssignModalFilterId(undefined)
  }

  const handleReverseTransaction = (transaction: TransactionDto) => {
    setReverseTransactionModal({ data: transaction, visible: true })
  }

  const handleSelectedClients = (event: SelectClientEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
    }
  }

  const handleSelectedWallets = (event: SelectWalletEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
    }
  }

  const handleSelectedUsersAssign = (event: SelectTransactionsEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
    }
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <>
      {isPaymentAgentModalOpen && (
        <Modal width={900} visible={isPaymentAgentModalOpen} onClose={handlePaymentAgentModalClose}>
          <SelectPaymentAgentTable
            filterId={paymentAgentModalFilterId}
            onSelect={handleSelectedPaymentAgents}
            onClose={handlePaymentAgentModalClose}
          />
        </Modal>
      )}
      {isClientModalOpen && (
        <Dialog open={isClientModalOpen} onOpenChange={(open) => !open && handleClientModalClose()}>
          <SelectClientTable
            filterId={clientModalFilterId}
            onSelect={handleSelectedClients}
            onClose={handleClientModalClose}
          />
        </Dialog>
      )}
      {isAssigneeModalOpen && (
        <Dialog
          open={isAssigneeModalOpen}
          onOpenChange={(open) => !open && handleSelectAssignUsersModalClose()}
        >
          <SelectTransactionsAssignTable
            filterId={assignModalFilterId}
            onMultipleSelect={handleSelectedUsersAssign}
            selectText='Select'
          />
        </Dialog>
      )}
      {isWalletModalOpen && (
        <Modal width={900} visible={isWalletModalOpen} onClose={handleWalletModalClose}>
          <SelectWalletTable
            filterId={walletModalFilterId}
            onSelect={handleSelectedWallets}
            onClose={handleWalletModalClose}
          />
        </Modal>
      )}
      {reverseTransactionModal.visible && !!reverseTransactionModal.data && (
        <Modal
          width={600}
          padding='3rem'
          visible={reverseTransactionModal.visible}
          onClose={handleReverseCompleteTransactionClose}
        >
          <WalletTransactionReversePage
            transaction={reverseTransactionModal.data}
            onClose={handleReverseCompleteTransactionClose}
          />
        </Modal>
      )}
      <Breadcrumbs
        crumbs={[
          { title: 'Transactions - Payment Agent', link: links.transactionPaymentAgent.url },
        ]}
      />
      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={FilterType.PaymentAgentWalletTransferOverview}
        filterFields={[
          {
            name: TransactionFilterKey.walletTransactionWalletPaymentAgent,
            label: 'Payment Agent',
            filter: (paymentAgentIdFilter: {
              filter: FilterDescriptor
              onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
            }) => (
              <SelectPaymentAgentFilter
                {...paymentAgentIdFilter}
                onPaymentAgentModalOpen={handlePaymentAgentModalOpen}
              />
            ),
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
          {
            name: TransactionFilterKey.walletTransactionWalletClient,
            label: 'Client',
            filter: (clientFilter: {
              filter: FilterDescriptor
              onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
            }) => (
              <SelectClientFilter {...clientFilter} onClientModalOpen={handleClientModalOpen} />
            ),
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
          {
            name: TransactionFilterKey.walletTransactionWallet,
            label: 'WalletId',
            filter: (walletFilter: {
              filter: FilterDescriptor
              onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
            }) => (
              <SelectWalletFilter {...walletFilter} onWalletModalOpen={handleWalletModalOpen} />
            ),
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
          {
            name: TransactionFilterKey.assignee,
            label: 'Assignee',
            filter: (assigneeFilter: {
              filter: FilterDescriptor
              onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
            }) => (
              <SelectAssigneeFilter
                {...assigneeFilter}
                onAssignModalOpen={handleAssigneeModalOpen}
              />
            ),
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
          ...getCrossWalletFields(),
        ]}
        containerClassName='wallet-table__filter'
        getQuery={getSearchQuery}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <TableWithExport data={transactions}>
          <Grid
            {...gridState}
            resizable
            data={transactions}
            sort={gridState.sort}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            onSortChange={handleSortChange}
            pageable
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={handleGridPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take || 10}
                skip={gridState.skip || 0}
              />
            )}
            style={{ zIndex: 0 }}
          >
            {columns
              .filter((c) => c.show)
              .map((column, idx) => (
                <Column
                  key={idx + 2}
                  columnMenu={(props) => (
                    <CustomColumnMenu
                      {...props}
                      columns={columns}
                      onColumnsSubmit={handleColumnsSubmit}
                    />
                  )}
                  {...column}
                />
              ))}
            <Column
              width={50}
              title=''
              cell={(props: GridCellProps) => {
                if (isTransactionCompletedStatus(props.dataItem.state.id)) {
                  return (
                    <td className={generateClassNameForReverseType(props.dataItem)}>
                      <MoreButton
                        items={[
                          {
                            name: 'Reverse',
                            disabled: !hasPermission(
                              permissions,
                              PermissionsEnum.CanReverseWalletDepositWithdrawalTransaction
                            ),
                            onClick: () => handleReverseTransaction(props.dataItem),
                          },
                        ]}
                      />
                    </td>
                  )
                }

                return <td className={generateClassNameForReverseType(props.dataItem)} />
              }}
            />
          </Grid>
        </TableWithExport>
      </TableStateContext.Provider>
    </>
  )
}

export default PaymentAgentTransactionsOverview
